import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Navigation } from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  ArrowLeft, Trash2, Search, RefreshCw, DollarSign, 
  Calendar, MapPin, User, Trophy, Pencil, X, Check
} from 'lucide-react';
import { toast } from 'sonner';

export default function ManageBundleWins() {
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [wins, setWins] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ winner_name: '', amount: '', course_name: '' });

  useEffect(() => {
    if (!loading && !user?.is_admin) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchWins();
    }
  }, [user]);

  const fetchWins = async () => {
    setLoadingData(true);
    try {
      const res = await fetch(`${API}/admin/bundle-wins?limit=200`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setWins(data.wins || []);
      }
    } catch (err) {
      toast.error('Failed to load bundle wins');
    } finally {
      setLoadingData(false);
    }
  };

  const handleDelete = async (win) => {
    if (!window.confirm(`Delete ${win.winner_name}'s $${win.amount} win?`)) return;
    
    setDeleting(win.id);
    try {
      const res = await fetch(
        `${API}/admin/bundle-win/${win.source}/${encodeURIComponent(win.id)}`,
        { method: 'DELETE', credentials: 'include' }
      );
      
      if (res.ok) {
        setWins(prev => prev.filter(w => !(w.id === win.id && w.source === win.source)));
        toast.success('Bundle win deleted');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete');
      }
    } catch (err) {
      toast.error('Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  const startEdit = (win) => {
    setEditing(win.id);
    setEditForm({
      winner_name: win.winner_name || '',
      amount: win.amount || '',
      course_name: win.course_name || ''
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditForm({ winner_name: '', amount: '', course_name: '' });
  };

  const handleEdit = async (win) => {
    try {
      const res = await fetch(
        `${API}/admin/bundle-win/${win.source}/${encodeURIComponent(win.id)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(editForm)
        }
      );
      
      if (res.ok) {
        setWins(prev => prev.map(w => {
          if (w.id === win.id && w.source === win.source) {
            return { ...w, ...editForm };
          }
          return w;
        }));
        toast.success('Bundle win updated');
        cancelEdit();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update');
      }
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const getSourceBadge = (source) => {
    const colors = {
      bundle_stories: 'bg-amber-100 text-amber-700',
      leaderboard_entries: 'bg-purple-100 text-purple-700',
      user_stats: 'bg-blue-100 text-blue-700',
    };
    const labels = {
      bundle_stories: 'Bundle Story',
      leaderboard_entries: 'Leaderboard',
      user_stats: 'User Stats',
    };
    return (
      <Badge className={`${colors[source] || 'bg-gray-100 text-gray-700'} text-xs`}>
        {labels[source] || source}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filteredWins = wins.filter(w => {
    const matchesSearch = !searchTerm || 
      w.winner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      w.course_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Calculate totals
  const totalWinnings = wins.reduce((sum, w) => sum + (parseFloat(w.amount) || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-bundle-cream flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bundle-green"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav">
      <Navigation />
      
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button 
            onClick={() => navigate('/admin')}
            className="p-2 hover:bg-white rounded-full"
          >
            <ArrowLeft className="w-5 h-5 text-bundle-green" />
          </button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-bundle-green">Manage Bundle Wins</h1>
            <p className="text-sm text-gray-600">Edit or delete bundle win records</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchWins}
            className="ml-auto"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={async () => {
              if (!window.confirm('DELETE ALL BUNDLE WINS? This will clear the Bundle Wins leaderboard. This cannot be undone!')) return;
              try {
                const res = await fetch(`${API}/admin/clear-all-bundle-wins`, {
                  method: 'DELETE',
                  credentials: 'include'
                });
                if (res.ok) {
                  const data = await res.json();
                  toast.success(`Deleted ${data.deleted_count} bundle wins`);
                  fetchWins();
                }
              } catch (err) {
                toast.error('Failed to clear bundle wins');
              }
            }}
            className="ml-2"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card className="bg-bundle-gold/20 border-bundle-gold">
            <CardContent className="p-4 text-center">
              <Trophy className="w-6 h-6 text-bundle-green mx-auto mb-2" />
              <p className="font-heading text-2xl font-bold text-bundle-green">{wins.length}</p>
              <p className="text-xs text-gray-600">Total Wins</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 text-center">
              <DollarSign className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <p className="font-heading text-2xl font-bold text-green-700">${totalWinnings.toFixed(0)}</p>
              <p className="text-xs text-green-600">Total Amount</p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by winner or course..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bundle-green focus:border-bundle-green"
              />
            </div>
          </CardContent>
        </Card>

        {/* Wins Table */}
        <Card>
          <CardHeader className="border-b border-gray-200">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-bundle-green" />
              All Bundle Wins ({filteredWins.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loadingData ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-bundle-green mx-auto"></div>
              </div>
            ) : filteredWins.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <Trophy className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No bundle wins found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredWins.map((win, idx) => (
                  <div 
                    key={`${win.source}-${win.id}-${idx}`}
                    className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    {editing === win.id ? (
                      // Edit mode
                      <>
                        <div className="flex-1 grid grid-cols-3 gap-3">
                          <Input
                            value={editForm.winner_name}
                            onChange={e => setEditForm(p => ({ ...p, winner_name: e.target.value }))}
                            placeholder="Winner name"
                            className="text-sm"
                          />
                          <Input
                            type="number"
                            value={editForm.amount}
                            onChange={e => setEditForm(p => ({ ...p, amount: e.target.value }))}
                            placeholder="Amount"
                            className="text-sm"
                          />
                          <Input
                            value={editForm.course_name}
                            onChange={e => setEditForm(p => ({ ...p, course_name: e.target.value }))}
                            placeholder="Course"
                            className="text-sm"
                          />
                        </div>
                        <button
                          onClick={() => handleEdit(win)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-full"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="p-2 text-gray-400 hover:bg-gray-100 rounded-full"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      // View mode
                      <>
                        <div className="w-40">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="font-medium text-gray-900 truncate">{win.winner_name || 'Unknown'}</span>
                          </div>
                        </div>
                        
                        <div className="w-24 text-center">
                          <span className="font-heading text-xl font-bold text-green-600">${win.amount}</span>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-700 truncate">{win.course_name || 'N/A'}</span>
                          </div>
                        </div>
                        
                        <div className="w-28 text-right">
                          <div className="flex items-center justify-end gap-1 text-sm text-gray-500">
                            <Calendar className="w-3 h-3" />
                            {formatDate(win.date)}
                          </div>
                        </div>
                        
                        <div className="w-28">
                          {getSourceBadge(win.source)}
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => startEdit(win)}
                            className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-full transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(win)}
                            disabled={deleting === win.id}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50"
                          >
                            {deleting === win.id ? (
                              <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
